import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { aoPerderSessao, definirToken, requisitar } from '../../shared/api/client.ts';
import type { Sessao, Usuario } from '../../shared/api/tipos.ts';

/**
 * Onde o token fica entre recarregamentos da página.
 *
 * `localStorage` é a escolha pragmática para um painel que fala com uma API
 * separada: sobrevive ao F5 e a abas novas. O custo conhecido é que um script
 * injetado na página consegue lê-lo — proteção real contra isso seria cookie
 * `HttpOnly` + `SameSite`, que exige o servidor na mesma origem e proteção
 * contra CSRF. Fica anotado como o caminho para quando isto for publicado.
 */
const CHAVE_TOKEN = 'contabil.token';

interface ContextoSessao {
  readonly usuario: Usuario | null;
  /** `true` enquanto o token guardado está sendo conferido no servidor. */
  readonly carregando: boolean;
  readonly entrar: (sessao: Sessao) => void;
  readonly sair: () => void;
  /** Atualiza o token mantendo a sessão — usado na troca de senha. */
  readonly renovar: (sessao: Sessao) => void;
}

const Contexto = createContext<ContextoSessao | null>(null);

function lerTokenGuardado(): string | null {
  try {
    return window.localStorage.getItem(CHAVE_TOKEN);
  } catch {
    // Navegação privada ou armazenamento bloqueado: segue sem sessão
    // guardada, em vez de a aplicação não abrir.
    return null;
  }
}

function guardarToken(token: string | null): void {
  try {
    if (token === null) {
      window.localStorage.removeItem(CHAVE_TOKEN);
    } else {
      window.localStorage.setItem(CHAVE_TOKEN, token);
    }
  } catch {
    // Sem persistência: a sessão vale só para esta aba, e é melhor que falhar.
  }
}

// O token precisa estar no cliente de API antes de qualquer requisição, e o
// primeiro `useQuery` pode disparar antes do primeiro efeito.
definirToken(lerTokenGuardado());

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const cache = useQueryClient();
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(lerTokenGuardado() !== null);

  const sair = useCallback(() => {
    definirToken(null);
    guardarToken(null);
    setUsuario(null);
    // Os dados em cache são do usuário que saiu; deixá-los faria a tela do
    // próximo login aparecer preenchida antes de qualquer resposta nova.
    cache.clear();
  }, [cache]);

  const entrar = useCallback((sessao: Sessao) => {
    definirToken(sessao.token);
    guardarToken(sessao.token);
    setUsuario(sessao.usuario);
  }, []);

  useEffect(() => {
    aoPerderSessao(sair);
  }, [sair]);

  // Um token guardado pode estar vencido, ter sido invalidado por troca de
  // senha ou pertencer a um acesso inativado. Quem decide é o servidor.
  useEffect(() => {
    const token = lerTokenGuardado();
    if (token === null) return;

    let cancelado = false;
    const controle = new AbortController();

    requisitar<Usuario>('/auth/eu', { sinal: controle.signal, trataProprio401: true })
      .then((eu) => {
        if (cancelado) return;
        definirToken(token);
        setUsuario(eu);
      })
      .catch(() => {
        if (cancelado) return;
        definirToken(null);
        guardarToken(null);
        setUsuario(null);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
      controle.abort();
    };
  }, []);

  const valor = useMemo<ContextoSessao>(
    () => ({ usuario, carregando, entrar, sair, renovar: entrar }),
    [usuario, carregando, entrar, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): ContextoSessao {
  const contexto = useContext(Contexto);
  if (contexto === null) {
    throw new Error('useSessao precisa estar dentro de <ProvedorSessao>.');
  }
  return contexto;
}
